/**
 * shell/useOverlayFocus.ts —— **焦点归还／Escape／真按钮**基线（壳规范 §1bis·五；判据 `ui_a11y_baseline`）。
 *
 * 规范逐字：**焦点归还**（关闭浮层／抽屉后还给**打开者**）｜真按钮（键盘／读屏可聚焦，`aria-expanded`）
 *          ｜`listbox`/`tablist`/`tree` 语义｜Escape 关闭并**焦点归还汉堡**。
 * 纪律：本件只操作焦点与键盘，不解释业务、不取数。
 */
import { useCallback, useEffect, useRef } from "react";

export interface OverlayFocus {
  /** 打开者元素（关闭后焦点归还目标） */
  openerRef: React.MutableRefObject<HTMLElement | null>;
  /** 浮层容器（供调用方挂 ref） */
  containerRef: React.MutableRefObject<HTMLElement | null>;
}

/**
 * 浮层开合配套：开 ⇒ 记下打开者；关 ⇒ **焦点归还打开者**；开期间 `Escape` ⇒ `onClose`。
 * `open` 恒 false 时不做任何事（常驻区域零副作用）。
 */
export function useOverlayFocus(open: boolean, onClose: () => void): OverlayFocus {
  const openerRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      const active = typeof document === "undefined" ? null : (document.activeElement as HTMLElement | null);
      openerRef.current = active && active !== document.body ? active : null;
      return;
    }
    const opener = openerRef.current;
    openerRef.current = null;
    if (opener && typeof opener.focus === "function") opener.focus();   // 焦点归还（不吞）
  }, [open]);

  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const focusFirst = useCallback((): void => {
    const container = containerRef.current;
    if (!container) return;
    const target = container.querySelector<HTMLElement>(
      "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
    );
    target?.focus();
  }, []);

  // 开 ⇒ 焦点移入浮层（键盘可达；读屏可感知）
  useEffect(() => {
    if (open) focusFirst();
  }, [open, focusFirst]);

  return { openerRef, containerRef };
}
